import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { observer } from 'mobx-react';
import { TournamentDTO, SportDTO, GenderDto, TournamentToSave } from 'screens/liveScoring/DTO';
import { stores } from 'mobx-store';
import { makeStyles } from '@material-ui/core/styles';
import ImageUploader from 'screens/liveScoring/components/uploadLogo';

interface TournamentFormProps {
  open: boolean;
  onClose: () => void;
  tournament: TournamentDTO | null;
}

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  nameShort: Yup.string().required('Short Name is required'),
  sport: Yup.object().shape({
    id: Yup.number().required('Sport is required')
  }).required('Sport is required'),
  gender: Yup.object().shape({
    id: Yup.number().required('Gender is required')
  }).required('Gender is required'),
  logo: Yup.string().nullable()
});

const TournamentForm: React.FC<TournamentFormProps> = observer(
  ({ open, onClose, tournament }) => {
    const classes = useStyles();
    const { liveScoringStore } = stores;

    const handleSubmit = async (values: TournamentDTO) => {
      try {
        const { gender, name, nameShort, sport, id, logo } = values;
        const toSave: TournamentToSave = { name, nameShort, logo, genderId: gender.id, sportId: sport.id || 0 };
        if (tournament?.id) {
          await liveScoringStore.updateTournament(id!, toSave);
        } else {
          await liveScoringStore.createTournament(toSave);
        }
        onClose();
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    };

    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{tournament ? 'Edit Tournament' : 'Add New Tournament'}</DialogTitle>
        <Formik
          initialValues={
            tournament || {
              name: '',
              nameShort: '',
              logo: '',
              sport: { id: 0, name: '', nameShort: '' },
              gender: { id: 0, title: '' }
            }
          } validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  name="name"
                  label="Name"
                  fullWidth
                  margin="normal"
                  error={touched.name && !!errors.name}
                  helperText={touched.name && errors.name}
                />
                <Field
                  as={TextField}
                  name="nameShort"
                  label="Short Name"
                  fullWidth
                  margin="normal"
                  error={touched.nameShort && !!errors.nameShort}
                  helperText={touched.nameShort && errors.nameShort}
                />
                <ImageUploader
                  name="logo"
                  label="Logo"
                  setFieldValue={setFieldValue}
                  logoUrl={values.logo}
                />

                <FormControl className={classes.formControl} error={touched.sport && !!errors.sport}>
                  <InputLabel id="sport-label">Sport</InputLabel>
                  <Select
                    labelId="sport-label"
                    name="sport.id"
                    value={values.sport?.id || ''}
                    onChange={(e) => {
                      const selectedSport = liveScoringStore.sports.find(s => s.id === e.target.value);
                      setFieldValue('sport', selectedSport);
                    }}
                  >
                    {liveScoringStore.sports.map((sport) => (
                      <MenuItem key={sport.id} value={sport.id}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl className={classes.formControl} error={touched.gender && !!errors.gender}>
                  <InputLabel id="gender-label">Gender</InputLabel>
                  <Select
                    labelId="gender-label"
                    name="gender.id"
                    value={values.gender?.id || ''}
                    onChange={(e) => {
                      const selectedGender = liveScoringStore.genders.find(g => g.id === e.target.value);
                      setFieldValue('gender', selectedGender);
                    }}
                  >
                    {liveScoringStore.genders.map((gender) => (
                      <MenuItem key={gender.id} value={gender.id}>
                        {gender.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} color="secondary">
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    );
  }
);

export default TournamentForm;